import { useState, useCallback } from 'react';
import Fetch from 'utils/fetch';
import { formatErrorMessage } from 'utils/handle-error';
import intl from 'react-intl-universal';

export const useFilterData = () => {
  const [loadingStates, setLoadingStates] = useState({
    materials: false,
    wallets: false,
    families: false,
    labeledFamilies: false
  });

  const [data, setData] = useState({
    materials: [],
    wallets: [],
    families: [],
    labeledFamilies: []
  });

  const [errors, setErrors] = useState({
    materials: null,
    wallets: null,
    families: null,
    labeledFamilies: null
  });

  const fetchWithLoading = async (key, fetchFunction) => {
    const abortController = new AbortController();
    
    setLoadingStates(prev => ({ ...prev, [key]: true }));
    try {
      const result = await fetchFunction(abortController.signal);
      setData(prev => ({ ...prev, [key]: result }));
      setErrors(prev => ({ ...prev, [key]: null }));
      return result;
    } catch (error) {
      if (error.name !== 'AbortError') {
        const errorMessage = (error.response && error.response.status === 400)
          ? formatErrorMessage(error)
          : intl.get('stocks.timeline.feedback.serverErrorOrder');
        
        setErrors(prev => ({ ...prev, [key]: errorMessage }));
      }
      throw error;
    } finally {
      setLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  const fetchMaterials = useCallback(async () => {
    return fetchWithLoading('materials', async (signal) => {
      const response = await Fetch.get('/materials:enabled', { signal });
      return response.data;
    });
  }, []);

  const fetchWallets = useCallback(async () => {
    return fetchWithLoading('wallets', async (signal) => {
      const response = await Fetch.get('/material-groups:all-with-active-materials', { signal });
      return response.data;
    });
  }, []);

  const fetchFamilies = useCallback(async () => {
    return fetchWithLoading('families', async (signal) => {
      const response = await Fetch.get('/families:all-with-active-materials', { signal });
      return response.data;
    });
  }, []);

  const fetchLabeledFamilies = useCallback(async () => {
    return fetchWithLoading('labeledFamilies', async (signal) => {
      const response = await Fetch.get('/labeled-families:all-with-active-materials', { signal });
      return response.data;
    });
  }, []);

  return {
    loadingStates,
    isLoading: Object.values(loadingStates).some(state => state),
    data,
    errors,
    actions: {
      fetchMaterials,
      fetchWallets,
      fetchFamilies,
      fetchLabeledFamilies
    }
  };
};
